'use client';

import type { UIEvent } from 'react';
import { useCallback } from 'react';
import { useMotionValue } from 'framer-motion';

// eslint-disable-next-line @typescript-eslint/naming-convention
const _getAlphaValue = (x: number) =>
  Math.min(1, Math.max(0, Number(x.toPrecision(2))));

type UseScrollShadowOptions = {
  topShadowRunwayHeight?: number;
};

export function useScrollShadow(options: UseScrollShadowOptions = {}) {
  const { topShadowRunwayHeight } = options;

  const opacityTop = useMotionValue(0);
  const opacityBottom = useMotionValue(0);

  const onScroll = useCallback(
    (evt: Pick<UIEvent<HTMLElement>, 'currentTarget'>) => {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { scrollHeight, scrollTop } = evt?.currentTarget;

      const scrollRate = 4;

      const topValue =
        // replace isNil with === null || === undefined
        topShadowRunwayHeight === null || topShadowRunwayHeight === undefined
          ? scrollTop / (scrollHeight / scrollRate)
          : scrollTop / topShadowRunwayHeight;
      const top = _getAlphaValue(topValue);
      opacityTop.set(top);

      const scrollBottom =
        scrollHeight -
        scrollTop -
        // topShadowRunwayHeight is possibly undefined so safely calculate it
        (topShadowRunwayHeight ?? 0);
      const bottomValue = scrollBottom / (scrollHeight / scrollRate);
      const bottom = _getAlphaValue(bottomValue);
      opacityBottom.set(bottom);
    },
    [opacityTop, opacityBottom, topShadowRunwayHeight]
  );

  return {
    opacity: opacityTop,
    topOpacity: opacityTop,
    bottomOpacity: opacityBottom,
    onScroll,
  };
}
